import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Platform } from '@angular/cdk/platform';
import { EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, expand, map, reduce, switchMap, takeWhile, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { APIListResponse } from '../models/apiresponse.model';
import {
  CampaignAttempt,
  CampaignCreateRequest,
  CampaignCreateResponse,
  CampaignPreviewRequest,
  CampaignPreviewResponse,
  CampaignReconciliation,
  CampaignRecipientRow,
  CampaignSendResponse,
  CampaignStatus,
  CampaignStatusPoll,
  CampaignSummary,
  GetCampaignAttemptsOptions,
  GetCampaignRecipientsOptions,
} from '../models/email-campaign.model';

export interface GetCampaignOptions {
  search?: string;
  status?: string;
  creator?: string;
  campaign_type?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
  page?: number;
}

const BASE_URL = () => `${environment.apiURL}/api/v1/admin/email-campaigns`;

// Consecutive failed status polls before giving up and reporting the check itself as
// failed, instead of polling forever in a state indistinguishable from "still working".
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

@Injectable({
  providedIn: 'root'
})
export class EmailCampaignService {

  constructor(
    private http: HttpClient,
    private _platform: Platform,
  ) {
  }

  preview(payload: CampaignPreviewRequest): Observable<CampaignPreviewResponse> {
    let headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.post<CampaignPreviewResponse>(
      `${BASE_URL()}/preview/`,
      payload,
      { headers }
    );
  }

  createCampaign(payload: CampaignCreateRequest): Observable<CampaignCreateResponse> {
    let headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.post<CampaignCreateResponse>(
      `${BASE_URL()}/`,
      payload,
      { headers }
    );
  }

  sendCampaign(campaignId: string, idempotencyKey: string): Observable<CampaignSendResponse> {
    let headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.post<CampaignSendResponse>(
      `${BASE_URL()}/${campaignId}/send/`,
      { idempotency_key: idempotencyKey },
      { headers }
    );
  }

  getStatus(campaignId: string): Observable<CampaignStatus> {
    let headers = new HttpHeaders();
    return this.http.get<CampaignStatus>(
      `${BASE_URL()}/${campaignId}/status/`,
      { headers }
    );
  }

  // Drives a single subscription that both polls and stops itself once the campaign
  // reaches a terminal state. Guarded so it never starts timers during SSR.
  //
  // A failed GET used to error the whole chain and silently stop all future polling,
  // leaving the UI frozen on stale data with no explanation. Each tick now catches its
  // own error, keeps the last known status on screen, and reports the failure via
  // `pollError` instead of ending the stream.
  //
  // But retrying forever on every failure is its own bug: if the server is genuinely down
  // (e.g. the broker the send/status endpoints depend on is unreachable), the bar would
  // poll indefinitely while never being terminal, which looks identical to a job that's
  // still legitimately in progress. After too many consecutive failures we give up and
  // emit `pollFailed: true` so the UI can say the check itself failed, instead of implying
  // the campaign is still being processed.
  pollStatus(campaignId: string, intervalMs: number = 1500): Observable<CampaignStatusPoll> {
    if (!this._platform.isBrowser) {
      return this.getStatus(campaignId).pipe(map((status) => ({ status, pollError: null, pollFailed: false })));
    }
    let lastKnownStatus: CampaignStatus = null;
    let consecutiveFailures = 0;
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getStatus(campaignId).pipe(
        map((status) => ({ status, pollError: null as string })),
        catchError((err) => {
          consecutiveFailures++;
          let message = err?.error?.detail || err?.message || 'Unable to reach the server';
          return lastKnownStatus
            ? of({ status: lastKnownStatus, pollError: message })
            : EMPTY;
        })
      )),
      tap((res) => {
        lastKnownStatus = res.status;
        if (!res.pollError) {
          consecutiveFailures = 0;
        }
      }),
      map((res) => ({ ...res, pollFailed: consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES })),
      takeWhile((res) => !res.status.is_terminal && !res.pollFailed, true)
    );
  }

  getAllCampaigns(options: GetCampaignOptions = {}): Observable<APIListResponse<CampaignSummary>> {
    let params = {};
    if (options.search) {
      params['search'] = options.search;
    }
    if (options.status) {
      params['status'] = options.status;
    }
    if (options.creator) {
      params['creator'] = options.creator;
    }
    if (options.campaign_type) {
      params['campaign_type'] = options.campaign_type;
    }
    if (options.ordering) {
      params['ordering'] = options.ordering;
    }
    if (options.limit) {
      params['limit'] = options.limit;
    }
    if (options.offset) {
      params['offset'] = options.offset;
    }
    if (options.page) {
      params['page'] = options.page;
    }
    let p = new HttpParams({ fromObject: params });
    let headers = new HttpHeaders();
    return this.http.get<APIListResponse<CampaignSummary>>(
      `${BASE_URL()}/`,
      { headers, params: p }
    );
  }

  getCampaign(campaignId: string): Observable<CampaignSummary> {
    let headers = new HttpHeaders();
    return this.http.get<CampaignSummary>(
      `${BASE_URL()}/${campaignId}/`,
      { headers }
    );
  }

  getRecipients(campaignId: string, options: GetCampaignRecipientsOptions = {}): Observable<APIListResponse<CampaignRecipientRow>> {
    let params = {};
    if (options.status) {
      params['status'] = options.status;
    }
    if (options.recipient_email) {
      params['recipient_email'] = options.recipient_email;
    }
    if (options.recipient_type) {
      params['recipient_type'] = options.recipient_type;
    }
    if (options.bcc_intended !== undefined && options.bcc_intended !== null) {
      params['bcc_intended'] = options.bcc_intended;
    }
    if (options.limit) {
      params['limit'] = options.limit;
    }
    if (options.offset) {
      params['offset'] = options.offset;
    }
    if (options.page) {
      params['page'] = options.page;
    }
    let p = new HttpParams({ fromObject: params });
    let headers = new HttpHeaders();
    return this.http.get<APIListResponse<CampaignRecipientRow>>(
      `${BASE_URL()}/${campaignId}/recipients/`,
      { headers, params: p }
    );
  }

  getAllRecipients(campaignId: string, options: GetCampaignRecipientsOptions = {}): Observable<CampaignRecipientRow[]> {
    return this.getRecipients(campaignId, options).pipe(
      expand((page) => page.next
        ? this.http.get<APIListResponse<CampaignRecipientRow>>(page.next)
        : EMPTY, 1),
      reduce((recipients: CampaignRecipientRow[], page) => recipients.concat(page.results), [])
    );
  }

  getRecipient(campaignId: string, recipientId: number): Observable<CampaignRecipientRow & { attempts: CampaignAttempt[] }> {
    let headers = new HttpHeaders();
    return this.http.get<CampaignRecipientRow & { attempts: CampaignAttempt[] }>(
      `${BASE_URL()}/${campaignId}/recipients/${recipientId}/`,
      { headers }
    );
  }

  getAttempts(campaignId: string, options: GetCampaignAttemptsOptions = {}): Observable<APIListResponse<CampaignAttempt>> {
    let params = {};
    if (options.status) {
      params['status'] = options.status;
    }
    if (options.recipient) {
      params['recipient'] = options.recipient;
    }
    if (options.limit) {
      params['limit'] = options.limit;
    }
    if (options.offset) {
      params['offset'] = options.offset;
    }
    if (options.page) {
      params['page'] = options.page;
    }
    let p = new HttpParams({ fromObject: params });
    let headers = new HttpHeaders();
    return this.http.get<APIListResponse<CampaignAttempt>>(
      `${BASE_URL()}/${campaignId}/attempts/`,
      { headers, params: p }
    );
  }

  getReconciliation(campaignId: string): Observable<CampaignReconciliation> {
    let headers = new HttpHeaders();
    return this.http.get<CampaignReconciliation>(
      `${BASE_URL()}/${campaignId}/reconciliation/`,
      { headers }
    );
  }

  cancelCampaign(campaignId: string): Observable<CampaignStatus> {
    let headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.post<CampaignStatus>(
      `${BASE_URL()}/${campaignId}/cancel/`,
      {},
      { headers }
    );
  }

  // Reserved for a future explicit selected-recipient retry policy - not wired to any
  // UI control yet, kept typed so the eventual feature only needs a caller.
  retryCampaign(campaignId: string, recipientIds: number[]): Observable<CampaignStatus> {
    let headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http.post<CampaignStatus>(
      `${BASE_URL()}/${campaignId}/retry/`,
      { recipient_ids: recipientIds },
      { headers }
    );
  }
}
